import { useDraggable } from '@dnd-kit/core'
import {
  Type, Image, Layout, AlignLeft, Video, MousePointerClick,
  LayoutGrid, Columns, Minus, Code, Quote, Rss, Star, DollarSign,
  CreditCard, BarChart2, Images, Building2, Mail, ShoppingBag, Lock,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { tenantCanUseProBlocks } from '@brix/types'
import type { BlockType } from '@brix/types'

interface PaletteBlock {
  type: BlockType
  label: string
  icon: typeof Type
  description: string
  group: string
  requiresPlan?: 'pro' // extend with more tiers if another gated block shows up
}

const PALETTE_BLOCKS: PaletteBlock[] = [
  // Layout
  { group: 'Layout', type: 'hero', label: 'Hero', icon: Layout, description: 'Full-width banner' },
  { group: 'Layout', type: 'cta', label: 'CTA', icon: MousePointerClick, description: 'Call to action' },
  { group: 'Layout', type: 'columns', label: 'Columns', icon: Columns, description: 'Multi-column layout' },
  { group: 'Layout', type: 'grid', label: 'Grid', icon: LayoutGrid, description: 'Equal-width content grid' },
  { group: 'Layout', type: 'spacer', label: 'Spacer', icon: Minus, description: 'Empty space' },
  { group: 'Layout', type: 'divider', label: 'Divider', icon: Minus, description: 'Horizontal rule' },
  // Content
  { group: 'Content', type: 'text', label: 'Text', icon: AlignLeft, description: 'Rich text' },
  { group: 'Content', type: 'quote', label: 'Pull Quote', icon: Quote, description: 'Large quotation' },
  { group: 'Content', type: 'cards', label: 'Cards', icon: CreditCard, description: 'Card grid (books, articles…)' },
  { group: 'Content', type: 'features', label: 'Features', icon: Star, description: 'Feature list' },
  { group: 'Content', type: 'stats', label: 'Stats', icon: BarChart2, description: 'Key numbers' },
  { group: 'Content', type: 'logos', label: 'Logos', icon: Building2, description: 'Logo strip' },
  { group: 'Content', type: 'testimonials', label: 'Testimonials', icon: Quote, description: 'Social proof' },
  { group: 'Content', type: 'blog-feed', label: 'Blog Feed', icon: Rss, description: 'Latest posts' },
  // Media
  { group: 'Media', type: 'image', label: 'Image', icon: Image, description: 'Single image' },
  { group: 'Media', type: 'gallery', label: 'Gallery', icon: Images, description: 'Image grid' },
  { group: 'Media', type: 'video', label: 'Video', icon: Video, description: 'Video player' },
  // Commerce
  { group: 'Commerce', type: 'pricing', label: 'Pricing', icon: DollarSign, description: 'Pricing tiers' },
  { group: 'Commerce', type: 'contact-form', label: 'Contact Form', icon: Mail, description: 'Contact / inquiry form' },
  { group: 'Commerce', type: 'product-catalog', label: 'Product Catalog', icon: ShoppingBag, description: 'Sell via external checkout', requiresPlan: 'pro' },
  // Code
  { group: 'Code', type: 'embed', label: 'Embed', icon: Code, description: 'Embed snippet' },
  { group: 'Code', type: 'html', label: 'HTML', icon: Code, description: 'Raw HTML' },
]

const GROUPS = ['Layout', 'Content', 'Media', 'Commerce', 'Code']

function DraggableBlock({ block, locked }: { block: PaletteBlock; locked: boolean }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${block.type}`,
    data: { type: 'palette', blockType: block.type },
    disabled: locked,
  })

  const Icon = block.icon

  if (locked) {
    return (
      <Link
        to="/billing"
        title="Requires the Pro plan — click to upgrade"
        className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5 text-left opacity-70 transition-opacity hover:opacity-100"
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100">
          <Icon className="h-3.5 w-3.5 text-gray-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-gray-500">{block.label}</p>
          <p className="truncate text-[11px] text-gray-400">{block.description}</p>
        </div>
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-violet-600">
          <Lock className="h-2.5 w-2.5" />
          Pro
        </span>
      </Link>
    )
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={[
        'flex cursor-grab items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left transition-all active:cursor-grabbing',
        isDragging ? 'opacity-50 shadow-lg' : 'hover:border-violet-300 hover:bg-violet-50',
      ].join(' ')}
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100">
        <Icon className="h-3.5 w-3.5 text-gray-500" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-800">{block.label}</p>
        <p className="truncate text-[11px] text-gray-400">{block.description}</p>
      </div>
    </div>
  )
}

export function BlockPalette() {
  const tenant = useAuthStore((s) => s.tenant)
  const isPro = !!tenant && tenantCanUseProBlocks(tenant)

  return (
    <aside className="flex w-56 shrink-0 flex-col border-r border-gray-200 bg-gray-50">
      <div className="border-b border-gray-200 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Blocks</p>
        <p className="mt-0.5 text-[11px] text-gray-400">Drag onto canvas</p>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        {GROUPS.map(group => {
          const items = PALETTE_BLOCKS.filter(b => b.group === group)
          return (
            <div key={group} className="mb-4">
              <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">{group}</p>
              <div className="space-y-1.5">
                {items.map(block => (
                  <DraggableBlock key={block.type} block={block} locked={block.requiresPlan === 'pro' && !isPro} />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </aside>
  )
}
